import { debounce } from 'lodash'
import { SimpleCache } from '@/utils/cache.utils'
import mixpanelUtils from '@/utils/mixpanel.utils'
import { runFlagged } from '@/services/flagGate'
import { deferMissingAnswerCounts, rememberDeferredAnswerCounts } from '@/utils/lazySlideAnswerCounts'

const cache = new SimpleCache()
const maxPresentationCacheTimeInSeconds = 6
const warmCachePresentationDetails = debounce((dispatch, { presentationId, payloadDataPresentation }) => {
  dispatch('getPresentationDetailsWithCacheAction', {
    presentationId,
    payloadDataPresentation,
    maxAgeInSeconds: maxPresentationCacheTimeInSeconds,
    isPreloading: true,
  })
}, 200)

const warmCachePresentationAttributes = debounce((dispatch, { presentationId }) => {
  dispatch('getPresentationAttributesWithCacheAction', {
    presentationId,
    maxAgeInSeconds: maxPresentationCacheTimeInSeconds,
    isPreloading: true,
  })
}, 200)

const getPresentationDetailsWithCacheOriginal = async ({ dispatch },
  {
    presentationId, payloadDataPresentation, maxAgeInSeconds, isPreloading,
  }) => {
  const action = 'fetchPresentationDetailsFromAPIAction'
  const res = await cache.invoke(() => {
    if (isPreloading) mixpanelUtils.track('Preload > Presentation details', { presentationId })
    return dispatch(
      action,
      { presentationId, payloadDataPresentation },
      { root: true },
    )
  }, {
    key: `${action}-${presentationId}`,
    maxAgeInSeconds,
  })
  // Hand every caller an isolated copy: the editor commits res.body into the
  // store and its destroyed() teardown clears state.presentation.id = null.
  // Because SimpleCache returns the SAME response object on a <6s hit, without
  // this copy that teardown write corrupts the object still held in the cache,
  // so a same-id re-open within the window returns a presentation with id=null
  // and the editor header gate (v-if="presentation.id && …") fails (LONG-699).
  // A NEW wrapper with a fresh shallow body — never a mutation of the cached
  // res — keeps the cached object pristine. Shallow is enough: the teardown
  // only nulls the top-level .id.
  return res && res.body ? { ...res, body: { ...res.body } } : res
}

const getPresentationDetailsWithCacheLazyAnswerCounts = async ({ dispatch },
  {
    presentationId, payloadDataPresentation, maxAgeInSeconds, isPreloading,
  }) => {
  const action = 'fetchPresentationDetailsFromAPIAction'
  const res = await cache.invoke(() => {
    if (isPreloading) mixpanelUtils.track('Preload > Presentation details', { presentationId })
    return dispatch(
      action,
      { presentationId, payloadDataPresentation: { ...payloadDataPresentation, skipAnswerCounts: true } },
      { root: true },
    ).then(deferMissingAnswerCounts)
  }, {
    key: `${action}-${presentationId}`,
    maxAgeInSeconds,
  })
  rememberDeferredAnswerCounts(res)
  return res && res.body ? { ...res, body: { ...res.body } } : res
}

const actions = {
  getPresentationAttributesWithCacheAction: async ({ dispatch },
    { presentationId, maxAgeInSeconds, isPreloading }) => {
    const action = 'presentationAttributeModule/fetchPresentationAttributes'
    return cache.invoke(() => {
      if (isPreloading) mixpanelUtils.track('Preload > Presentation attributes', { presentationId })
      return dispatch(
        action,
        presentationId,
        { root: true },
      )
    }, {
      key: `${action}-${presentationId}`,
      maxAgeInSeconds,
    })
  },
  getPresentationDetailsWithCacheAction: (context, payload) => runFlagged(
    'SYST-929-lazy-slide-answer-counts',
    () => getPresentationDetailsWithCacheLazyAnswerCounts(context, payload),
    () => getPresentationDetailsWithCacheOriginal(context, payload),
  ),

  // Drop the cached presentation-details entry so the next fetch — by this page
  // or a later editor open — goes to the network. Needed after a reset-results
  // new-session swap (AHA-41694): the backend regenerates slide ids, but this 6s
  // cache (also warmed on list hover) would otherwise serve the pre-swap slides.
  // Key must match the one built in getPresentationDetailsWithCacheAction.
  purgePresentationDetailsCacheAction: (_, presentationId) => {
    cache.purge(`fetchPresentationDetailsFromAPIAction-${presentationId}`)
  },

  // Restamp a response that has already landed, so the next read hits even when the request outlived maxAgeInSeconds.
  primePresentationDetailsCacheAction: (_, { presentationId, response }) => {
    const key = `fetchPresentationDetailsFromAPIAction-${presentationId}`
    cache.purge(key)
    return cache.invoke(async () => response, { key })
  },

  purgePresentationAttributesCacheAction: (_, presentationId) => {
    cache.purge(`presentationAttributeModule/fetchPresentationAttributes-${presentationId}`)
  },

  primePresentationAttributesCacheAction: (_, { presentationId, response }) => {
    const key = `presentationAttributeModule/fetchPresentationAttributes-${presentationId}`
    cache.purge(key)
    return cache.invoke(async () => response, { key })
  },

  // Inspect the cached presentation-details body without fetching. Returns null
  // when nothing live is cached (or the warm request failed), so a caller can tell
  // "the cache would serve this" from "the cache would go to the network".
  // Key must match the one built in getPresentationDetailsWithCacheAction.
  peekCachedPresentationDetailsAction: async (_, presentationId) => {
    const cached = cache.peek(
      `fetchPresentationDetailsFromAPIAction-${presentationId}`,
      maxPresentationCacheTimeInSeconds,
    )
    if (!cached) return null

    try {
      const res = await cached
      return (res && res.body) || null
    } catch (e) {
      return null
    }
  },

  preWarmCachePresentationDetailsAction: ({ dispatch }, { presentationId, payloadDataPresentation }) => {
    warmCachePresentationDetails(dispatch, { presentationId, payloadDataPresentation })
  },

  preWarmCachePresentationAttributesAction: ({ dispatch }, { presentationId }) => {
    warmCachePresentationAttributes(dispatch, { presentationId })
  },
}
const getters = {
  maxPresentationCacheTimeInSeconds: () => maxPresentationCacheTimeInSeconds,
}

export default {
  namespaced: true,
  actions,
  getters,
}
